import React, { useState } from 'react';
import { CheckCircle2, MessageCircle, Phone } from 'lucide-react';
import { BRAND, waLink } from './brand';
import { submitEnquiry } from './api';
import HoneyPot from './HoneyPot';

/**
 * Shared enquiry block for service pages (cruise, car rental, flights…).
 * `fields` adds extra inputs: [{ key, label, type, options, placeholder, required }]
 */
const ServiceEnquiry = ({ id = 'enquire', kicker = 'Get a quote', title, productType, subject, fields = [], points = [], defaults = {} }) => {
  const base = { name: '', phone: '', email: '', date: '', travelers: '', message: '' };
  fields.forEach((x) => { base[x.key] = ''; });
  const [f, setF] = useState({ ...base, ...defaults });
  const [state, setState] = useState({ busy: false, ok: false, err: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const today = new Date().toISOString().split('T')[0];

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, ok: false, err: '' });
    const extra = fields.map((x) => (f[x.key] ? `${x.label}: ${f[x.key]}` : null)).filter(Boolean);
    try {
      await submitEnquiry({
        name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim(),
        destination: f.destination || f.to || '', travelDate: f.date, travelers: f.travelers,
        message: [subject, ...extra, f.message].filter(Boolean).join('\n'), productType,
      });
      setState({ busy: false, ok: true, err: '' });
      setF({ ...base, ...defaults });
    } catch {
      setState({ busy: false, ok: false, err: `Sorry, that didn't go through. Please WhatsApp or call ${BRAND.phone}.` });
    }
  };

  const input = (x) => {
    const common = { id: `${id}-${x.key}`, value: f[x.key], onChange: set(x.key), required: !!x.required };
    if (x.options) {
      return (
        <select {...common}>
          <option value="">{x.placeholder || 'Select'}</option>
          {x.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    return <input {...common} type={x.type || 'text'} placeholder={x.placeholder || ''} />;
  };

  // Pair extra fields into rows of two
  const rows = [];
  for (let i = 0; i < fields.length; i += 2) rows.push(fields.slice(i, i + 2));

  return (
    <section className="vk-section" id={id} style={{ scrollMarginTop: 'calc(var(--header-h) + 12px)' }}>
      <div className="vk-wrap vk-split vk-split--form">
        <div className="vk-panel" style={{ padding: 28 }}>
          <p className="vk-kicker">{kicker}</p>
          <h2 className="vk-display" style={{ fontSize: 'clamp(30px,4vw,44px)', margin: '0 0 20px' }}>{title}</h2>
          {state.ok ? (
            <div className="vk-alert vk-alert--ok" role="status">Thank you — our team will call you back shortly with options.</div>
          ) : (
            <form className="vk-form" onSubmit={submit}><HoneyPot />
              <div className="vk-form__row">
                <div className="vk-field"><label htmlFor={`${id}-name`}>Full name *</label><input id={`${id}-name`} required value={f.name} onChange={set('name')} autoComplete="name" /></div>
                <div className="vk-field"><label htmlFor={`${id}-phone`}>Phone *</label><input id={`${id}-phone`} required type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" /></div>
              </div>
              <div className="vk-field"><label htmlFor={`${id}-email`}>Email *</label><input id={`${id}-email`} required type="email" value={f.email} onChange={set('email')} autoComplete="email" /></div>
              {rows.map((row) => (
                <div key={row.map((x) => x.key).join('-')} className={row.length === 2 ? 'vk-form__row' : undefined}>
                  {row.map((x) => <div key={x.key} className="vk-field"><label htmlFor={`${id}-${x.key}`}>{x.label}{x.required ? ' *' : ''}</label>{input(x)}</div>)}
                </div>
              ))}
              <div className="vk-form__row">
                <div className="vk-field"><label htmlFor={`${id}-date`}>Date</label><input id={`${id}-date`} type="date" min={today} value={f.date} onChange={set('date')} /></div>
                <div className="vk-field"><label htmlFor={`${id}-trav`}>Travellers</label><input id={`${id}-trav`} type="number" min="1" max="200" value={f.travelers} onChange={set('travelers')} /></div>
              </div>
              <div className="vk-field"><label htmlFor={`${id}-msg`}>Anything else?</label><textarea id={`${id}-msg`} rows={3} value={f.message} onChange={set('message')} /></div>
              {state.err ? <div className="vk-alert vk-alert--err" role="alert">{state.err}</div> : null}
              <button type="submit" className="vk-btn vk-btn--hot" disabled={state.busy}>{state.busy ? 'Sending…' : 'Get my quote'}</button>
            </form>
          )}
        </div>
        <div style={{ display: 'grid', gap: 14, alignContent: 'start' }}>
          {points.length ? (
            <div className="vk-panel vk-panel--sand">
              <h3>Good to know</h3>
              <ul className="vk-ticks">{points.map((p) => <li key={p}><CheckCircle2 size={18} color="var(--hot)" /> {p}</li>)}</ul>
            </div>
          ) : null}
          <div className="vk-panel">
            <h3>Prefer to talk?</h3>
            <div style={{ display: 'grid', gap: 10 }}>
              <a className="vk-btn vk-btn--whatsapp vk-btn--block" href={waLink(subject)} target="_blank" rel="noopener noreferrer"><MessageCircle size={18} /> WhatsApp us</a>
              <a className="vk-btn vk-btn--outline vk-btn--block" href={BRAND.phoneLink}><Phone size={18} /> {BRAND.phone}</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceEnquiry;
